import React from 'react';
import { useTranslation } from 'react-i18next';
import { ConverterType } from '../types';
import { useCountriesAndBanks } from '../hooks/useCountriesAndBanks';
import BankDropdown from './BankDropdown';
import { bankIdToConverter } from '../utils/converterResolution';

interface CountryBankSelectorProps {
  onConverterChange: (converter: ConverterType) => void;
  activeConverter?: ConverterType;
}

const CountryBankSelector: React.FC<CountryBankSelectorProps> = ({
  onConverterChange,
  activeConverter
}) => {
  const { t } = useTranslation();
  const {
    countries,
    banks,
    selectedCountry,
    selectedBank,
    loading,
    error,
    setSelectedCountry,
    setSelectedBank,
  } = useCountriesAndBanks();

  // Set converter when both country and bank are selected.
  // Omit onConverterChange from deps to avoid "Maximum update depth" – the parent's
  // handleConverterChange is not memoized, so it would change every render and retrigger this effect.
  React.useEffect(() => {
    if (selectedCountry && selectedBank) {
      const converter = bankIdToConverter(selectedBank);
      if (converter) {
        onConverterChange(converter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when selection changes; omit onConverterChange to avoid loop
  }, [selectedCountry, selectedBank]);

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const countryId = event.target.value || null;
    setSelectedCountry(countryId);
  };

  const handleBankSelect = (bankId: string) => {
    setSelectedBank(bankId);
  };

  // If we have an active converter, show a compact summary view
  if (activeConverter && selectedCountry && selectedBank) {
    const selectedCountryData = countries.find(c => c.id === selectedCountry);
    const selectedBankData = banks.find(b => b.id === selectedBank);
    
    return (
      <div className="country-bank-selector compact">
        <div className="selection-summary">
          <div className="summary-header">
            <h3>{t('currentSelection', { ns: 'tools' })}</h3>
            <button 
              className="change-selection-btn"
              onClick={() => {
                setSelectedCountry(null);
                setSelectedBank(null);
                onConverterChange('');
              }}
            >
              {t('changeSelection', { ns: 'tools' })}
            </button>
          </div>
          <div className="summary-content">
            <div className="summary-item">
              <span className="summary-label">{t('country', { ns: 'tools' })}:</span>
              <span className="summary-value">
                {selectedCountryData?.flag} {selectedCountryData?.name}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('bank', { ns: 'tools' })}:</span>
              <span className="summary-value">
                {selectedBankData?.name || selectedBank}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="country-bank-selector">
      <div className="selection-container">
        <div className="country-selection">
          <label htmlFor="country-select" className="selection-label">
            {t('selectCountry', { ns: 'tools' })}
          </label>
          <select
            id="country-select"
            value={selectedCountry || ''}
            onChange={handleCountryChange}
            className="country-dropdown"
          >
            <option value="">{t('chooseCountry', { ns: 'tools' })}</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.flag} {country.name}
              </option>
            ))}
          </select>
        </div>

        {selectedCountry && (
          <div className="bank-selection">
            <label className="selection-label">
              {t('selectBank', { ns: 'tools' })}
            </label>
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>{t('loadingBanks', { ns: 'tools' })}</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>{t('error', { ns: 'tools' })}: {error}</p>
              </div>
            ) : (
              <BankDropdown
                banks={banks}
                selectedBank={selectedBank}
                onBankSelect={handleBankSelect}
                placeholder={t('chooseBank', { ns: 'tools' })}
              />
            )}
          </div>
        )}

        {selectedCountry && selectedBank && (
          <div className="selection-complete">
            <div className="success-message">
              <span className="check-icon">✅</span>
              <span>{t('selectionComplete', { ns: 'tools' })}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CountryBankSelector; 